// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * game.ts — the tower, as an actual physics simulation (Tricky-Towers style).
 *
 * There is no grid. A brick descends from the top of the play area under gravity
 * while you steer it left/right (freely, pixel by pixel) and rotate it. The
 * moment it touches the pile it locks out of your control and the physics takes
 * over — it settles, leans, and can slide off. You lose height by TOPPLING, not
 * by filling a space. When a brick comes to rest (or falls off) the next one
 * appears at the top.
 *
 * The physics runs on planck.js (a Box2D port) — a far stiffer stacking solver
 * than the old one, so towers lean and topple believably instead of springing.
 * We drive it on a FIXED 60Hz timestep (never a variable dt) and use no
 * Math.random, so the sim is deterministic and the balance sim can referee it.
 * Determinism does NOT need to hold across peers: multiplayer is a parallel
 * same-seed race where each peer sims only its own tower, so float divergence
 * between machines is harmless (peers agree only on the brick order).
 *
 * We run planck directly in SCREEN coordinates (x right, y down) scaled to
 * metres, with gravity pointing +y. Box2D's maths is orientation-agnostic, so
 * this is valid and keeps the render/camera/geometry all in one y-down frame —
 * no flips, and body.getAngle() drops straight into a canvas rotate().
 */

import { World, Vec2, Box, type Body as PBody } from 'planck';
import { brickAt, cellsAt, shapeExtent, type Shape } from './pieces';
import type { Mode } from './modes';

// ---- world constants (world px; 1 cell = CELL px) --------------------------
export const CELL = 26;
export const PLAY_W = 300;
export const PLINTH_TOP_Y = 620;
const PLINTH_BOTTOM_Y = 736;
const KILL_Y = 812;                 // a brick centre past this has fallen off
const SPAWN_ABOVE = 7.5 * CELL;     // how far above the tower top a brick appears
const NUDGE = 7;                    // keyboard steer step (world px)

// planck runs in metres; this is the px↔m scale (keeps bricks ~0.3–2 m).
const MPP = 0.02;
const toM = (px: number): number => px * MPP;
const toPx = (m: number): number => m / MPP;

const FIXED_MS = 1000 / 60;
const FIXED_S = 1 / 60;
const VEL_ITERS = 8;
const POS_ITERS = 3;
const MAX_SUBSTEPS = 8;

const GRAVITY = 9;                  // m/s² (screen-down)
const FALL_CAP = 2.6;               // m/s — gentle steady descent speed
const FAST_FALL_CAP = 6;            // m/s — while the Drop button/Down is held.
                                    // Kept well above FALL_CAP so a drop still
                                    // saves real time, but low enough that it
                                    // arrives rather than slams the pile (#3).
const STEER_SPEED = 6.0;            // m/s — how fast the brick tracks your finger
const STEER_GAIN = 12;              // how hard it homes on the target x

// rest / settle detection (planck units)
const REST_LIN = 0.06;              // m/s
const REST_ANG = 0.10;              // rad/s
const REST_FRAMES = 16;             // ~0.27s of stillness = settled

// sabotage
const CHARGE_COST = 3;              // settled bricks earned per gust charge
const MAX_CHARGES = 2;
const WIND_MS = 1300;
const WIND_ACCEL = 6.5;             // horizontal accel on a FALLING brick (m/s²)

// end conditions
const COLLAPSE_MIN = 3;             // must build this tall before a collapse ends it
const HEIGHT_SCORE = 100;           // score per cell of max height

export type Phase = 'active' | 'over';

export type GameEvent =
  | { t: 'spawn' }
  | { t: 'land'; speed: number; x: number; y: number }
  | { t: 'lost'; wasSettled: boolean; x: number; y: number }
  | { t: 'rotate' }
  | { t: 'charge' }
  | { t: 'gust' }        // we spent a charge (local juice)
  | { t: 'attack' }      // outgoing sabotage — the runner routes it to the leader
  | { t: 'wind' }        // incoming wind now blowing our falling brick
  | { t: 'near' }        // the tower is a nudge from going (slow-mo cue)
  | { t: 'win' }
  | { t: 'over' };

interface Cell { lx: number; ly: number; } // local offset in metres

interface Brick {
  body: PBody;
  shape: Shape;
  cells: Cell[];        // local cell centres (metres) for geometry
  settled: boolean;
  active: boolean;      // the one currently under player control / falling
}

export interface Pt { x: number; y: number; }

export interface BrickPoly {
  quads: Array<[Pt, Pt, Pt, Pt]>;   // world px (y-down)
  tint: number;
  settled: boolean;
  active: boolean;
}

export interface GameSnapshot {
  h: number;        // current height in cells (rounded to 0.1)
  placed: number;   // bricks released
  lost: number;     // bricks fallen off
  alive: boolean;
}

export interface GameOpts {
  mode: Mode;
  seed: number;
}

const HALF = toM(CELL * 0.98) / 2;   // brick cell half-extent (metres)

export class Game {
  readonly mode: Mode;
  readonly seed: number;
  readonly plinthW: number;

  private world: World;
  private plinth: PBody;
  private bricks: Brick[] = [];
  private events: GameEvent[] = [];

  phase: Phase = 'active';

  // the live, player-controlled falling brick
  private active: Brick | null = null;
  private activeShape: Shape;
  private activeRot = 0;
  private targetX: number;          // steer target (world px)
  private landed = false;           // touched the pile → control released
  private fastDrop = false;         // Drop held → descend fast
  private restCount = 0;
  private fallSpeed = 0;            // active brick's descent speed this substep
  private lastImpact = 0;           // descent speed at the moment it met the pile
  private nearCooldown = 0;

  private index = 0;                 // next brick index in the seeded stream
  drops = 0;                         // bricks released
  settledCount = 0;                  // bricks currently resting on the tower
  lostCount = 0;                     // bricks fallen off
  private chargeProg = 0;
  charges = 0;
  won = false;

  private simMs = 0;
  private windUntil = 0;
  private windDir = 0;
  private acc = 0;

  private curHeight = 0;             // cells
  private maxHeight = 0;             // cells (the score basis)

  constructor(opts: GameOpts) {
    this.mode = opts.mode;
    this.seed = opts.seed >>> 0;
    this.plinthW = opts.mode.plinthW * CELL;

    this.world = new World({ gravity: new Vec2(0, GRAVITY) });

    const py = (PLINTH_TOP_Y + PLINTH_BOTTOM_Y) / 2;
    this.plinth = this.world.createBody({ type: 'static', position: new Vec2(toM(PLAY_W / 2), toM(py)) });
    this.plinth.createFixture(new Box(toM(this.plinthW) / 2, toM(PLINTH_BOTTOM_Y - PLINTH_TOP_Y) / 2), {
      friction: 0.9,
      restitution: 0,
    });

    this.activeShape = brickAt(this.seed, this.index);
    this.targetX = PLAY_W / 2;
    this.spawnActive();
  }

  // -------------------------------------------------------------- geometry ---
  /** Smallest y (highest point) over settled bricks, or the plinth top. */
  private towerTopY(): number {
    let top = PLINTH_TOP_Y;
    for (const b of this.bricks) {
      if (!b.settled) continue;
      const t = this.brickTopY(b);
      if (t < top) top = t;
    }
    return top;
  }

  /** Highest (smallest-y) cell corner of a brick, in world px. */
  private brickTopY(b: Brick): number {
    let top = Infinity;
    for (const c of b.cells) {
      for (const cy of [-HALF, HALF]) {
        for (const cx of [-HALF, HALF]) {
          const p = b.body.getWorldPoint(new Vec2(c.lx + cx, c.ly + cy));
          const y = toPx(p.y);
          if (y < top) top = y;
        }
      }
    }
    return top;
  }

  private brickCentreX(b: Brick): number { return toPx(b.body.getPosition().x); }
  private brickCentreY(b: Brick): number { return toPx(b.body.getPosition().y); }

  private aimRange(): { min: number; max: number } {
    const { w } = shapeExtent(this.activeShape, this.activeRot);
    const half = (w * CELL) / 2;
    return { min: half + 6, max: PLAY_W - half - 6 };
  }

  // -------------------------------------------------------------- spawning ---
  private spawnActive(): void {
    const shape = this.activeShape;
    const cells = cellsAt(shape, 0).map(([cx, cy]) => ({ lx: toM(cx * CELL), ly: toM(cy * CELL) }));
    const spawnX = PLAY_W / 2;
    const spawnY = this.towerTopY() - SPAWN_ABOVE;
    this.targetX = spawnX;
    this.activeRot = 0;

    const body = this.world.createBody({
      type: 'dynamic',
      position: new Vec2(toM(spawnX), toM(spawnY)),
      angle: 0,
      fixedRotation: false,
      linearDamping: 0.02,
      angularDamping: 0.04,
      bullet: true,               // continuous collision so a fast brick can't tunnel
    });
    for (const c of cells) {
      body.createFixture(new Box(HALF, HALF, new Vec2(c.lx, c.ly), 0), {
        density: 1,
        friction: 0.95,
        restitution: 0.0,
      });
    }

    const brick: Brick = { body, shape, cells, settled: false, active: true };
    this.bricks.push(brick);
    this.active = brick;
    this.landed = false;
    this.fastDrop = false;
    this.restCount = 0;
    this.events.push({ t: 'spawn' });
  }

  private nextBrick(): void {
    this.index += 1;
    this.activeShape = brickAt(this.seed, this.index);
    this.spawnActive();
  }

  // -------------------------------------------------------------- controls ---
  /** Absolute steer (drag): aim the falling brick at this x, clamped. */
  aimTo(worldX: number): void {
    if (this.phase !== 'active' || !this.active || this.landed) return;
    const r = this.aimRange();
    this.targetX = Math.max(r.min, Math.min(r.max, worldX));
  }

  /** Relative steer (keyboard). */
  nudge(dir: number): void {
    if (this.phase !== 'active' || !this.active || this.landed) return;
    this.aimTo(this.targetX + Math.sign(dir) * NUDGE);
  }

  /**
   * Discrete one-cell nudge — a precision aiming aid (the ◀ ▶ buttons). Steers
   * the target a fixed brick-width left/right so you can line the active brick up
   * exactly beside another, which fine drag-aiming is fiddly for on a phone
   * (feedback #4). It only ever moves the brick you are currently placing — it
   * never repositions a brick that has already dropped.
   */
  nudgeStep(dir: number): void {
    if (this.phase !== 'active' || !this.active || this.landed) return;
    this.aimTo(this.targetX + Math.sign(dir) * CELL);
  }

  /** Rotate the falling brick a quarter turn. */
  rotate(dir: number): void {
    if (this.phase !== 'active' || !this.active || this.landed) return;
    this.activeRot = (((this.activeRot + Math.sign(dir)) % 4) + 4) % 4;
    const b = this.active.body;
    b.setTransform(b.getPosition(), (this.activeRot * Math.PI) / 2);
    b.setAngularVelocity(0);
    b.setAwake(true);
    // A rotation can change the width and push the target out of range.
    this.aimTo(this.targetX);
    this.events.push({ t: 'rotate' });
  }

  /** Speed the descent up (Drop button / Down). Not an instant release. */
  drop(): void {
    if (this.phase !== 'active' || !this.active || this.landed) return;
    this.fastDrop = true;
  }

  // -------------------------------------------------------------- sabotage ---
  /** Spend a charge to blow a crosswind at the leader. Returns true if spent. */
  gust(): boolean {
    if (this.phase === 'over' || this.charges <= 0) return false;
    this.charges -= 1;
    this.events.push({ t: 'gust' });
    this.events.push({ t: 'attack' });
    return true;
  }

  /**
   * Wind arriving from a rival. It only ever pushes the brick that is CURRENTLY
   * FALLING and not yet landed — never a settled one — so it cannot topple a
   * stable tower. That bound is the whole safety argument for the mechanic
   * (principle #18d) and the mechanism test asserts it.
   */
  receiveWind(dir: number): void {
    if (this.phase === 'over') return;
    this.windDir = dir >= 0 ? 1 : -1;
    this.windUntil = this.simMs + WIND_MS;
    this.events.push({ t: 'wind' });
  }

  windActive(): boolean { return this.simMs < this.windUntil; }

  // ------------------------------------------------------------------ step ---
  private phaseNow(): Phase { return this.phase; }

  step(dtMs: number): void {
    if (this.phase === 'over') return;
    this.acc += dtMs;
    let guard = 0;
    while (this.acc >= FIXED_MS && guard < MAX_SUBSTEPS) {
      this.acc -= FIXED_MS;
      this.substep();
      guard += 1;
      if (this.phaseNow() === 'over') break;
    }
    if (guard >= MAX_SUBSTEPS) this.acc = 0; // never spiral
  }

  /** One fixed physics tick — the ONLY place the world advances. */
  private substep(): void {
    this.simMs += FIXED_MS;
    const a = this.active;

    if (a && !this.landed) {
      const body = a.body;
      const vel = body.getLinearVelocity();

      // Steer horizontally toward the target x (velocity control, so the pile
      // still stops it — you cannot shove a brick through the tower).
      const dxPx = this.targetX - this.brickCentreX(a);
      let vx = Math.max(-STEER_SPEED, Math.min(STEER_SPEED, toM(dxPx) * STEER_GAIN));
      if (this.windActive()) vx += this.windDir * WIND_ACCEL * FIXED_S;

      // Cap the fall speed for a gentle, controllable descent. FAST_FALL_CAP is
      // held low enough that a held drop arrives without slamming the pile (#3).
      const cap = this.fastDrop ? FAST_FALL_CAP : FALL_CAP;
      const vy = Math.min(vel.y, cap);
      body.setLinearVelocity(new Vec2(vx, vy));
      body.setAwake(true);
      // Remember the descent speed going into the solver: the contact is created
      // AND resolved inside world.step(), which kills the velocity, so this
      // pre-step value is the true speed at which the brick meets the pile — the
      // "landing energy" the fall cap governs.
      this.fallSpeed = Math.max(0, vy);
    }

    this.world.step(FIXED_S, VEL_ITERS, POS_ITERS);

    if (a) {
      // Off the bottom / far off the side → lost.
      const cx = this.brickCentreX(a);
      const cy = this.brickCentreY(a);
      if (cy > KILL_Y || Math.abs(cx - PLAY_W / 2) > 1200) {
        this.loseActive();
      } else {
        if (!this.landed && this.activeTouching()) {
          this.lastImpact = this.fallSpeed;  // the speed it met the pile at
          this.landed = true;                // control released; physics owns it now
        }
        if (this.landed) {
          const speed = this.bodySpeed(a.body);
          const asp = Math.abs(a.body.getAngularVelocity());
          if (speed < REST_LIN && asp < REST_ANG) {
            this.restCount += 1;
            if (this.restCount >= REST_FRAMES) this.settleActive();
          } else {
            this.restCount = 0;
          }
        }
      }
    }

    this.checkFallen();
    this.detectNear();
  }

  /** Is the active brick resting against the plinth or a settled brick? */
  private activeTouching(): boolean {
    const a = this.active;
    if (!a) return false;
    for (let ce = a.body.getContactList(); ce; ce = ce.next ?? null) {
      const c = ce.contact;
      if (c && c.isTouching()) return true;
    }
    return false;
  }

  private bodySpeed(body: PBody): number {
    const v = body.getLinearVelocity();
    return Math.hypot(v.x, v.y);
  }

  private detectNear(): void {
    if (this.nearCooldown > 0) { this.nearCooldown -= 1; return; }
    for (const br of this.bricks) {
      if (!br.settled) continue;
      if (this.bodySpeed(br.body) > 0.9 || Math.abs(br.body.getAngularVelocity()) > 1.4) {
        this.events.push({ t: 'near' });
        this.nearCooldown = 40;
        return;
      }
    }
  }

  private settleActive(): void {
    const a = this.active;
    if (!a) return;
    a.settled = true;
    a.active = false;
    this.drops += 1;           // a brick is "used" once it resolves (settled or lost)
    this.settledCount += 1;
    const speed = this.bodySpeed(a.body);
    this.chargeProg += 1;
    if (this.chargeProg >= CHARGE_COST) {
      this.chargeProg = 0;
      if (this.charges < MAX_CHARGES) { this.charges += 1; this.events.push({ t: 'charge' }); }
    }
    this.events.push({ t: 'land', speed, x: this.brickCentreX(a), y: this.brickTopY(a) });
    this.active = null;
    this.landed = false;
    this.restCount = 0;
    this.recomputeHeight();
    this.checkEnd();
    if (this.phaseNow() !== 'over') this.nextBrick();
  }

  private loseActive(): void {
    const a = this.active;
    if (!a) return;
    const lx = this.brickCentreX(a);
    const ly = this.brickCentreY(a);
    this.removeBrick(a);
    this.active = null;
    this.landed = false;
    this.restCount = 0;
    this.drops += 1;           // a brick is "used" once it resolves (settled or lost)
    this.lostCount += 1;
    this.events.push({ t: 'lost', wasSettled: false, x: lx, y: ly });
    this.recomputeHeight();
    this.checkEnd();
    if (this.phaseNow() !== 'over') this.nextBrick();
  }

  /** Remove any settled brick that has since fallen off. */
  private checkFallen(): void {
    for (let i = this.bricks.length - 1; i >= 0; i--) {
      const br = this.bricks[i];
      if (br.active) continue; // the falling one is handled in substep
      const cx = this.brickCentreX(br);
      const cy = this.brickCentreY(br);
      if (cy > KILL_Y || Math.abs(cx - PLAY_W / 2) > 1200) {
        const wasSettled = br.settled;
        this.removeBrick(br);
        this.lostCount += 1;
        if (wasSettled) this.settledCount = Math.max(0, this.settledCount - 1);
        this.events.push({ t: 'lost', wasSettled, x: cx, y: cy });
        this.recomputeHeight();
        this.checkCollapse();
      }
    }
  }

  private removeBrick(br: Brick | null): void {
    if (!br) return;
    this.world.destroyBody(br.body);
    const i = this.bricks.indexOf(br);
    if (i >= 0) this.bricks.splice(i, 1);
  }

  // ------------------------------------------------------------- height/end ---
  private recomputeHeight(): void {
    let top = PLINTH_TOP_Y;
    let any = false;
    for (const b of this.bricks) {
      if (!b.settled) continue;
      any = true;
      const t = this.brickTopY(b);
      if (t < top) top = t;
    }
    this.curHeight = any ? Math.max(0, (PLINTH_TOP_Y - top) / CELL) : 0;
    if (this.curHeight > this.maxHeight) this.maxHeight = this.curHeight;
  }

  private checkEnd(): void {
    if (this.mode.end === 'goal' && this.curHeight >= this.mode.goalCells) {
      this.won = true;
      this.finish(true);
      return;
    }
    if (this.mode.end === 'budget' && this.drops >= this.mode.budget) {
      this.finish(false);
      return;
    }
    if (this.drops >= this.mode.budget) this.finish(false);
  }

  private checkCollapse(): void {
    if (this.mode.end !== 'collapse') return;
    if (this.maxHeight >= COLLAPSE_MIN && this.curHeight < this.mode.collapseFrac * this.maxHeight) {
      this.finish(false);
    }
  }

  private finish(won: boolean): void {
    if (this.phase === 'over') return;
    this.phase = 'over';
    if (won) this.events.push({ t: 'win' });
    this.events.push({ t: 'over' });
  }

  // -------------------------------------------------------------- queries ---
  get over(): boolean { return this.phase === 'over'; }

  get score(): number {
    let s = Math.round(this.maxHeight * HEIGHT_SCORE);
    if (this.mode.end === 'goal' && this.won) {
      s += 500 + Math.max(0, this.mode.budget - this.drops) * 150;
    }
    return s;
  }

  get heightCells(): number { return Math.round(this.curHeight * 10) / 10; }
  get maxHeightCells(): number { return Math.round(this.maxHeight * 10) / 10; }
  get heightCm(): number { return Math.round(this.maxHeight * 20); }

  snapshot(): GameSnapshot {
    return {
      h: this.heightCells,
      placed: this.drops,
      lost: this.lostCount,
      alive: this.phase !== 'over',
    };
  }

  // ---- render geometry (engine-neutral) -----------------------------------
  /** Every brick as a set of world-px cell quads — the renderer draws these. */
  brickPolys(): BrickPoly[] {
    return this.bricks.map((br) => {
      const quads = br.cells.map((c) => {
        const corners: Array<[number, number]> = [
          [c.lx - HALF, c.ly - HALF],
          [c.lx + HALF, c.ly - HALF],
          [c.lx + HALF, c.ly + HALF],
          [c.lx - HALF, c.ly + HALF],
        ];
        const pts = corners.map(([lx, ly]) => {
          const p = br.body.getWorldPoint(new Vec2(lx, ly));
          return { x: toPx(p.x), y: toPx(p.y) };
        });
        return [pts[0], pts[1], pts[2], pts[3]] as [Pt, Pt, Pt, Pt];
      });
      return { quads, tint: br.shape.tint, settled: br.settled, active: br.active };
    });
  }

  /** Highest painted point across all bricks (world px) — for the camera. */
  topWorldY(): number {
    let top = PLINTH_TOP_Y;
    for (const b of this.bricks) {
      const t = this.brickTopY(b);
      if (t < top) top = t;
    }
    return top;
  }

  /** The falling brick's x and current y, for the drop guide. Null once landed. */
  activeGuide(): { x: number; y: number } | null {
    if (!this.active || this.landed) return null;
    return { x: this.brickCentreX(this.active), y: this.brickCentreY(this.active) };
  }

  goalCells(): number { return this.mode.goalCells; }

  worldConst(): { CELL: number; PLAY_W: number; PLINTH_TOP_Y: number; PLINTH_BOTTOM_Y: number; KILL_Y: number; plinthW: number } {
    return { CELL, PLAY_W, PLINTH_TOP_Y, PLINTH_BOTTOM_Y, KILL_Y, plinthW: this.plinthW };
  }

  /** Where the settled tower's mass sits — used by the bot to aim. */
  support(): { centroidX: number; topY: number; minX: number; maxX: number } {
    let sx = 0;
    let n = 0;
    let top = PLINTH_TOP_Y;
    let minX = PLAY_W / 2;
    let maxX = PLAY_W / 2;
    let any = false;
    for (const b of this.bricks) {
      if (!b.settled) continue;
      any = true;
      const cx = this.brickCentreX(b);
      sx += cx;
      n += 1;
      const t = this.brickTopY(b);
      if (t < top) top = t;
      // approx horizontal extent from cell centres ± a half cell
      for (const c of b.cells) {
        const p = b.body.getWorldPoint(new Vec2(c.lx, c.ly));
        const px = toPx(p.x);
        if (px - CELL / 2 < minX) minX = px - CELL / 2;
        if (px + CELL / 2 > maxX) maxX = px + CELL / 2;
      }
    }
    if (!any) return { centroidX: PLAY_W / 2, topY: PLINTH_TOP_Y, minX: PLAY_W / 2 - this.plinthW / 2, maxX: PLAY_W / 2 + this.plinthW / 2 };
    return { centroidX: sx / n, topY: top, minX, maxX };
  }

  // ---- accessors the bot/sim aim with -------------------------------------
  curShape(): Shape { return this.activeShape; }
  curRot(): number { return this.activeRot; }
  aimBounds(): { min: number; max: number } { return this.aimRange(); }
  aimX(): number { return this.active ? this.brickCentreX(this.active) : this.targetX; }

  drainEvents(): GameEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  peekEvents(): readonly GameEvent[] { return this.events; }

  // ---- sim helpers (headless balance / mechanism tests) --------------------
  /**
   * Steer the current brick to `targetX`, fast-drop it, and step until it lands
   * and the pile reacts. Deterministic. Returns when a new brick is falling in
   * open air, or the game is over. This is the sim/bot's one-brick primitive.
   */
  dropAndSettle(maxSteps = 600, tail = 45): void {
    if (this.phase !== 'active' || !this.active) return;
    const startDrops = this.drops;
    // A careful placement: steer into line and let the brick settle at the gentle
    // descent speed (no slam). This is how a good human plays and keeps the sim
    // representative of real, forgiving stacking.
    let steps = 0;
    while (this.phaseNow() !== 'over' && this.drops === startDrops && steps < maxSteps) {
      this.substep();
      steps += 1;
    }
    // let any knock-on collapse register while the next brick is still high up
    for (let i = 0; i < tail && this.phaseNow() !== 'over'; i++) this.substep();
  }

  /** Step exactly one fixed tick — for the mechanism/determinism tests. */
  stepOnce(): void { if (this.phase !== 'over') this.substep(); }

  /** Speed at which the active brick last touched the pile (0 until it lands). */
  impactSpeed(): number { return this.lastImpact; }

  /** Positions of every brick body — for the mechanism test's independent audit. */
  bodyPositions(): Array<{ x: number; y: number; angle: number; settled: boolean }> {
    return this.bricks.map((b) => ({
      x: this.brickCentreX(b),
      y: this.brickCentreY(b),
      angle: b.body.getAngle(),
      settled: b.settled,
    }));
  }
}
